import { Close } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import React, { useCallback, useMemo, useState } from 'react';
import type { SegmentedControllerItemProps } from '@withjoy/joykit';
import { Box, Flex, SegmentedController, TextV2, TooltipV2 } from '@withjoy/joykit';

import { useTranslation } from '@shared/core';
import { getSessionStorage } from '@shared/core/storage';
import { MobileProductTabsRoot } from './TravelProductToggle.styles';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useTravelProductToggleTelemetry } from './TravelProductToggle.telemetry';
import type { TravelProductId, TravelProductToggleProps } from './TravelProductToggle.types';

const PRODUCT_ORDER: ReadonlyArray<TravelProductId> = ['hotels', 'cars'];
const CARS_PROMO_TOOLTIP_SEEN_KEY = 'joy:travelCarsPromoTooltipSeen';

type ProductSegmentProps = Readonly<{
  label: string;
  isActive: boolean;
  onClick: () => void;
}>;

const ProductSegment: React.FC<ProductSegmentProps> = ({ isActive, label, onClick }) => (
  <Flex
    as="button"
    role="radio"
    paddingX={5}
    border="none"
    type="button"
    cursor="pointer"
    onClick={onClick}
    alignItems="center"
    height={pxToRem(36)}
    justifyContent="center"
    aria-checked={isActive}
    borderRadius={pxToRem(200)}
    tabIndex={isActive ? 0 : -1}
    transition="background-color 0.2s ease"
    backgroundColor={isActive ? 'mono2' : 'transparent'}
  >
    <TextV2
      color="mono14"
      whiteSpace="nowrap"
      typographyVariant="button1"
      fontWeight={isActive ? 600 : 400}
    >
      {label}
    </TextV2>
  </Flex>
);

type MobileProductTabsProps = Readonly<{
  'data-testid'?: string;
  activeProduct: TravelProductId;
  onSelect: (product: TravelProductId) => void;
}>;

// Mobile presentation: full-width underlined tabs centered via SegmentedController.
const MobileProductTabs = React.forwardRef<HTMLDivElement, MobileProductTabsProps>(({ activeProduct, onSelect, 'data-testid': dataTestId }, ref) => {
  const { tq } = useTranslation('accommodationsShared');

  const items = useMemo<ReadonlyArray<SegmentedControllerItemProps>>(
    () => [
      { label: 'hotels', value: tq`Hotels`, isActive: activeProduct === 'hotels', onClick: () => onSelect('hotels') },
      { label: 'cars', value: tq`Car Rentals`, isActive: activeProduct === 'cars', onClick: () => onSelect('cars') },
    ],
    [activeProduct, onSelect, tq],
  );

  return (
    <MobileProductTabsRoot
      ref={ref}
      data-testid={dataTestId}
    >
      <SegmentedController
        isMobile
        items={items}
        variant="underline"
      />
    </MobileProductTabsRoot>
  );
});

MobileProductTabs.displayName = 'MobileProductTabs';

type CarsPromoTooltipProps = Readonly<{
  isOpen: boolean;
  onDismiss: () => void;
  children: React.ReactElement;
}>;

// Dismissible promo tooltip advertising car rentals.
const CarsPromoTooltip: React.FC<CarsPromoTooltipProps> = ({ isOpen, onDismiss, children }) => {
  const { tq } = useTranslation('accommodationsShared');

  return (
    <TooltipV2
      isOpen={isOpen}
      triggerType="none"
      placement="bottom"
      content={
        <Flex
          gap={2}
          alignItems="flex-start"
          maxWidth={pxToRem(260)}
        >
          <Box>
            <TextV2
              color="mono4"
              fontWeight={600}
              typographyVariant="label2"
            >
              {tq`Getting to the event?`}
            </TextV2>
            <TextV2
              color="mono4"
              typographyVariant="label2"
            >
              {tq`Add a car rental at guest-only rates.`}
            </TextV2>
          </Box>
          <Box
            as="button"
            padding={0}
            color="mono4"
            type="button"
            border="none"
            display="flex"
            flexShrink={0}
            cursor="pointer"
            alignItems="center"
            onClick={onDismiss}
            justifyContent="center"
            backgroundColor="transparent"
            aria-label={tq`Close tooltip`}
          >
            <Close size="sm" />
          </Box>
        </Flex>
      }
    >
      {children}
    </TooltipV2>
  );
};

const TravelProductToggleInner: React.FC<TravelProductToggleProps> = ({ activeProduct, onProductChange, showCarsPromoTooltip, 'data-testid': dataTestId }) => {
  const { tq } = useTranslation('accommodationsShared');
  const isMobileOrTablet = useIsMobileOrTablet();
  const telemetry = useTravelProductToggleTelemetry();

  const storage = getSessionStorage();
  const [isPromoDismissed, setIsPromoDismissed] = useState(() => storage.getItem(CARS_PROMO_TOOLTIP_SEEN_KEY) === 'true');
  const isPromoOpen = !!showCarsPromoTooltip && activeProduct === 'hotels' && !isPromoDismissed;

  const dismissPromo = useCallback(() => {
    setIsPromoDismissed(true);
    storage.setItem(CARS_PROMO_TOOLTIP_SEEN_KEY, 'true');
    telemetry.productToggleTooltipDismissed({ product: activeProduct });
  }, [activeProduct, storage, telemetry]);

  const handleSelect = useCallback(
    (product: TravelProductId) => {
      if (product === activeProduct) {
        return;
      }
      onProductChange(product);
    },
    [activeProduct, onProductChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const currentIndex = PRODUCT_ORDER.indexOf(activeProduct);
      let nextIndex = -1;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % PRODUCT_ORDER.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + PRODUCT_ORDER.length) % PRODUCT_ORDER.length;
      }

      if (nextIndex < 0) {
        return;
      }

      event.preventDefault();
      event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')[nextIndex]?.focus();
      handleSelect(PRODUCT_ORDER[nextIndex]);
    },
    [activeProduct, handleSelect],
  );

  const testId = dataTestId ?? 'travel-product-toggle';

  if (isMobileOrTablet) {
    return (
      <CarsPromoTooltip
        isOpen={isPromoOpen}
        onDismiss={dismissPromo}
      >
        <MobileProductTabs
          data-testid={testId}
          onSelect={handleSelect}
          activeProduct={activeProduct}
        />
      </CarsPromoTooltip>
    );
  }

  return (
    <Flex
      flexShrink={0}
      role="radiogroup"
      border="1px solid"
      borderColor="mono3"
      alignItems="center"
      padding={pxToRem(4)}
      data-testid={testId}
      backgroundColor="white"
      onKeyDown={handleKeyDown}
      borderRadius={pxToRem(200)}
      aria-label={tq`Search product`}
      boxShadow={`0 ${pxToRem(2)} ${pxToRem(8)} rgba(0, 0, 0, 0.1)`}
    >
      <ProductSegment
        label={tq`Hotels`}
        isActive={activeProduct === 'hotels'}
        onClick={() => handleSelect('hotels')}
      />
      <CarsPromoTooltip
        isOpen={isPromoOpen}
        onDismiss={dismissPromo}
      >
        <Box display="inline-flex">
          <ProductSegment
            label={tq`Cars`}
            isActive={activeProduct === 'cars'}
            onClick={() => handleSelect('cars')}
          />
        </Box>
      </CarsPromoTooltip>
    </Flex>
  );
};

export const TravelProductToggle = React.memo(TravelProductToggleInner);
